import { useState, useEffect } from 'react';
import PostCard from './PostCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { Loader2 } from 'lucide-react';

export default function PostList({ fetchFunction, onPostUpdate, isGuardian = false }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadPosts(1);
  }, [fetchFunction]);

  const loadPosts = async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchFunction(pageNum, 20);
      
      if (append) {
        setPosts(prev => [...prev, ...response.results]);
      } else {
        setPosts(response.results || []);
      }
      
      setPage(response.page || pageNum);
      setTotalPages(response.total_pages || 1);
      setHasMore((response.page || pageNum) < (response.total_pages || 1));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load posts');
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      loadPosts(page + 1, true);
    }
  };

  const handlePostUpdate = () => {
    if (onPostUpdate) {
      onPostUpdate();
    }
    // Refresh current page
    loadPosts(page, false);
  };

  if (loading && posts.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--green-bright))]" />
      </div>
    );
  }

  if (error && posts.length === 0) {
    return (
      <EmptyState
        title="Error loading posts"
        description={error}
      />
    );
  }

  if (posts.length === 0) {
    return (
      <EmptyState
        title="No posts yet"
        description="Be the first to share something with the community!"
      />
    );
  }

  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <PostCard
          key={post.id}
          post={post}
          onUpdate={handlePostUpdate}
          isGuardian={isGuardian}
        />
      ))}
      
      {hasMore && (
        <div className="flex justify-center pt-4">
          <button
            onClick={loadMore}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-[hsl(var(--green-bright))] hover:underline disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </span>
            ) : (
              'Load more posts'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
